import React, { useEffect, useRef } from "react";

import { useScreenDimensions } from "@main/pages/shared/consumers/useScreenDimensions";
import { eventWithTime } from "@rrweb/types";
import rrwebPlayer from "rrweb-player";

const useSessionReplay = (sessionReplayUrl: string) => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [events, setEvents] = React.useState<eventWithTime[]>([]);
  const replayContainerRef = useRef<HTMLDivElement | null>(null);
  const replayerRef = useRef<rrwebPlayer | null>(null);
  const { isMobileView } = useScreenDimensions();

  useEffect(() => {
    const inputFocusListener = () => {
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA"))
        replayerRef?.current?.getReplayer().pause();
    };

    window.addEventListener("focus", inputFocusListener, true);

    return () => {
      window.removeEventListener("focus", inputFocusListener, true);
    };
  }, [replayerRef]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchSessionReplay = async () => {
      try {
        setLoading(true);

        if (sessionReplayUrl) {
          const response = await fetch(sessionReplayUrl, {
            method: "GET",
            mode: "cors",
            signal: abortController.signal,
          });
          const jsonEvents = await response.text();
          setEvents(JSON.parse(jsonEvents));
        }
      } catch (e) {
        console.error("Error loading session replay", e);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionReplay();

    return () => {
      abortController.abort();
    };
  }, [sessionReplayUrl]);

  useEffect(() => {
    if (!loading && events.length > 0 && replayContainerRef.current) {
      const container: HTMLDivElement = replayContainerRef.current;

      const newReplayer = new rrwebPlayer({
        target: container,
        props: {
          events: events,
          width: isMobileView ? 320 : 640,
          height: isMobileView ? 200 : 400,
          speedOption: [1, 2, 4, 8],
        },
      });
      replayerRef.current = newReplayer;

      return () => {
        newReplayer.getReplayer().destroy();
        replayerRef.current = null;
        container.innerHTML = "";
      };
    }
  }, [loading, events, isMobileView]);

  return { loading, replayContainerRef };
};

export { useSessionReplay };
